import { z } from 'zod'
import { stereoMixSettingsSchema } from '@customTypes/cloudmix/common/stereoMix'
import {
  POSITION_TYPE,
  positionSchema,
} from '@customTypes/cloudmix/common/position'
import { cropSchema } from '@customTypes/cloudmix/common/crop'
import {
  fallbackBehaviourSchema,
  FallbackBehaviourType,
} from '@customTypes/cloudmix/common/fallbackBehaviour'

export const displayNameSchema = z.string().nullish()
export type DisplayName = z.infer<typeof displayNameSchema>

export const audioVideoPadSchema = z.object({
  name: z.string(),
  displayName: displayNameSchema,
})
export type AudioVideoPad = z.infer<typeof audioVideoPadSchema>

export const audioVideoOutputPadWithFallbackSchema = audioVideoPadSchema.extend(
  {
    fallbackBehaviour: fallbackBehaviourSchema
      .default({
        type: FallbackBehaviourType.black,
      })
      .nullish(),
  }
)

export const mixerInputVideoSchema = z.object({
  active: z.boolean().nullable().default(false),
  alpha: z.number({ coerce: true }).min(0).max(1).nullable().default(1),
  crop: cropSchema.nullable().default({
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  }),
  order: z.number({ coerce: true }).nullable().default(0),
  position: positionSchema.nullable().default({
    x: 0,
    y: 0,
    positionType: POSITION_TYPE.normalised,
  }),
  scale: z.number({ coerce: true }).min(0).max(2).nullable().default(1),
})
export type MixerInputVideo = z.infer<typeof mixerInputVideoSchema>

export const mixerInputAudioSchema = z.object({
  mute: z.boolean().nullable().default(true),
  volume: z.number().nullable().default(1),
  stereoMix: stereoMixSettingsSchema.nullable().default({
    leftToLeft: 1,
    leftToRight: 0,
    rightToLeft: 0,
    rightToRight: 1,
  }),
})
export type MixerInputAudio = z.infer<typeof mixerInputAudioSchema>

export const mixerInputStateSchema = z.object({
  audioLevel: z.object({
    channels: z.array(
      z.object({
        decay: z.number(),
        rms: z.number(),
        peak: z.number(),
      })
    ),
    duration: z.number(),
  }),
})
export type MixerInputState = z.infer<typeof mixerInputStateSchema>

export const mixerOutputPadSchema = z.object({
  name: z.string(),
  displayName: z.string(),
})

export const baseMixerInputPadSchema = audioVideoPadSchema.extend({
  video: mixerInputVideoSchema,
  audio: mixerInputAudioSchema,
})
export type BaseMixerInputPad = z.infer<typeof baseMixerInputPadSchema>

export const projectMixerInputPadSchema = baseMixerInputPadSchema
export type ProjectMixerInputPad = z.infer<typeof projectMixerInputPadSchema>

export const mixerInputPadSchema = baseMixerInputPadSchema.extend({
  inputState: mixerInputStateSchema.optional(),
})
export type MixerInputPad = z.infer<typeof mixerInputPadSchema>

export const padSchema = z.union([
  mixerInputPadSchema,
  audioVideoPadSchema,
  audioVideoOutputPadWithFallbackSchema,
])
export type Pad = z.infer<typeof padSchema>

export const partialPadSchema = z.union([
  mixerInputPadSchema.partial(),
  audioVideoPadSchema.partial(),
  audioVideoOutputPadWithFallbackSchema.partial(),
])
export type PartialPad = z.infer<typeof partialPadSchema>

// Type guards
export function isMixerInputPad(pad: Pad): pad is MixerInputPad {
  return (
    'video' in pad &&
    'audio' in pad &&
    typeof (pad as any).video === 'object' && // Ensure 'video' is an object
    typeof (pad as any).audio === 'object'
  )
}

export function isAudioVideoPad(pad: Pad): pad is AudioVideoPad {
  return 'name' in pad && 'displayName' in pad
}
